import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  LoadingOrbit,
  Text,
} from '@chatsdet/ui';
import type { TestSuiteMemberDetail } from './types';

export function getNativeSuiteRunCounts(members: TestSuiteMemberDetail[]) {
  const layered = members.filter((member) => member.layerIndex !== null);
  return {
    layeredCount: layered.length,
    degradedCount: layered.filter((member) => member.health === 'degraded').length,
  };
}

export function RunNativeSuiteDialog({
  isOpen,
  onOpenChange,
  suiteName,
  members,
  isLoading = false,
  isPending = false,
  onConfirm,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  suiteName: string;
  members: TestSuiteMemberDetail[];
  isLoading?: boolean;
  isPending?: boolean;
  onConfirm: (reEnableDegraded: boolean) => void;
}) {
  const [reEnableDegraded, setReEnableDegraded] = React.useState(false);
  const { layeredCount, degradedCount } = getNativeSuiteRunCounts(members);

  React.useEffect(() => {
    setReEnableDegraded(false);
  }, [isOpen, suiteName]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="md">
      <span />
      <Dialog title={`Run ${suiteName}?`}>
        <DialogContent>
          {isLoading ? (
            <div className="flex justify-center py-6">
              <LoadingOrbit size="md" />
            </div>
          ) : (
            <div className="space-y-3">
              <Text size="sm" className="text-muted-foreground">
                {layeredCount} workflow{layeredCount === 1 ? '' : 's'} selected.
                {degradedCount > 0 && !reEnableDegraded
                  ? ` ${degradedCount} degraded flow${degradedCount === 1 ? '' : 's'} will be skipped.`
                  : null}
              </Text>
              {degradedCount > 0 && (
                <div className="rounded-md border border-border bg-card p-3">
                  <Checkbox
                    isSelected={reEnableDegraded}
                    onChange={setReEnableDegraded}
                    label="Re-enable degraded flows for this run"
                    description={`Reset and run ${degradedCount} degraded flow${degradedCount === 1 ? '' : 's'} in this suite.`}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isDisabled={isLoading || isPending || layeredCount === 0}
            onPress={() => onConfirm(reEnableDegraded && degradedCount > 0)}
          >
            Run suite
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
